import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {IAContextMeter} from '../ia_assistant/IAContextMeter';

// El "prompt del momento" rota por contexto temporal: cierre de mes en los últimos días,
// el día a día el resto del tiempo (D10).
const MONTH_CLOSE_FROM_DAY = 25;

// Card "IA" del Panel (ficha v2, D10): estado de conexión MCP + contexto de la IA + un prompt
// accionable para el momento del mes. El fetch de la biblioteca se hace UNA vez y alimenta
// también al medidor (prop `library`).
export function IAPreviewBox({clientId}) {
    const {t} = useTranslation();
    const [library, setLibrary] = useState(null);
    const [mcpUsed, setMcpUsed] = useState(null); // null = sin dato (no se muestra la línea)
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const url = `${API_URL}agent/prompt-library/?agency_client_id=${clientId}`;
        new APIClient(url).call('GET').then((data) => {
            if (data && data.available_ingredients) {
                setLibrary({
                    available_ingredients: data.available_ingredients,
                    prompts: data.prompts || [],
                });
            } else {
                setFailed(true);
            }
        });
        // Estado de conexión (D10): el starter pack expone mcp_recently_used (uso del conector
        // en 30 días, agency-level). Si falla, la línea simplemente no se muestra.
        new APIClient(`${API_URL}agent/client_starter_pack/?agency_client_id=${clientId}`)
            .call('GET').then((data) => {
                if (data && data.mcp_recently_used !== undefined) {
                    setMcpUsed(data.mcp_recently_used === true);
                }
            });
    }, [clientId]);

    const momentPrompt = useMemo(() => {
        if (!library) return null;
        const momento = new Date().getDate() >= MONTH_CLOSE_FROM_DAY ? 'cierre_mes' : 'dia_a_dia';
        // lock.enabled=true significa prompt HABILITADO (lock_for: enabled = not missing).
        return library.prompts.find(
            (p) => (p.momentos || []).includes(momento) && (!p.lock || p.lock.enabled)) || null;
    }, [library]);

    const handleCopy = () => {
        if (!momentPrompt) return;
        navigator.clipboard.writeText(momentPrompt.prompt_text).then(() => {
            toast.success(t('control_central.ia_prompt_copied'));
            // El uso se registra solo si la copia realmente ocurrió (patrón PromptLibraryCard).
            new APIClient(`${API_URL}agent/prompt-library/${momentPrompt.id}/used/`)
                .call('POST', {agency_client_id: clientId});
        }).catch(() => {
            toast.error(t('prompt_library.copy_error'));
        });
    };

    if (failed) return null; // sin módulo / error: la card no aporta

    return (
        <div className="box ia-preview-box">
            <div className="activity-log-preview-header">
                <h3 className="activity-log-preview-title">{t('control_central.ia_title')}</h3>
                <Link className="memory-preview-link" to={`/client/${clientId}/ia`}>
                    {t('control_central.ia_open')} &rarr;
                </Link>
            </div>

            {mcpUsed !== null && (
                <p className={`ia-preview-mcp ${mcpUsed ? '-connected' : ''}`}>
                    {mcpUsed
                        ? <><i className="fas fa-check"/> {t('control_central.ia_mcp_connected')}</>
                        : (
                            <Link to={`/client/${clientId}/ia/connect`}>
                                {t('control_central.ia_mcp_pending')} &rarr;
                            </Link>
                        )}
                </p>
            )}

            {library && <IAContextMeter clientId={clientId} library={library} compact hideTitle/>}

            {momentPrompt && (
                <div className="ia-preview-prompt">
                    <span className="ia-preview-prompt-label">{t('control_central.ia_prompt_of_moment')}</span>
                    <p className="ia-preview-prompt-title">{momentPrompt.title}</p>
                    <button className="btn -alt -negative -small" onClick={handleCopy}>
                        {t('control_central.ia_prompt_copy')}
                    </button>
                </div>
            )}
        </div>
    );
}
